import axios from 'axios'
import type { AuthResponse, LoginCredentials, SignupData } from '@/types/auth'
import type { components } from '@/types/api'

import { appConfig } from '@/types/env'

export const API_URL: string = appConfig.API_URL

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})
api.defaults.xsrfCookieName = 'csrftoken'
api.defaults.xsrfHeaderName = 'X-CSRFToken'

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const cookies = document.cookie ? document.cookie.split('; ') : []
  for (const cookie of cookies) {
    const [cookieName, ...valueParts] = cookie.split('=')
    if (cookieName === name) {
      return decodeURIComponent(valueParts.join('='))
    }
  }
  return null
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])

api.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase() ?? 'GET'
  if (!SAFE_METHODS.has(method)) {
    const token = getCookie(api.defaults.xsrfCookieName ?? 'csrftoken')
    if (token && config.headers) {
      const headerName = api.defaults.xsrfHeaderName ?? 'X-CSRFToken'
      config.headers.set(headerName, token)
    }
  }
  return config
})

// API types
export interface Skill {
  id: number
  name: string
}

export interface Profile {
  id: number
  username: string
  bio?: string
  skills?: Skill[]
  hourly_rate?: number
  company?: string
  location?: string
  avatar_url?: string | null
}

export interface UserProfileResponse {
  id: number
  user: {
    id: number
    username: string
    email?: string | null
    first_name?: string
    last_name?: string
  }
  role: 'customer' | 'developer'
  bio?: string | null
  skills?: Skill[]
  hourly_rate?: string | null
  company?: string
  location?: string
  email_verified: boolean
  email_verified_at?: string | null
  avatar_url?: string | null
}

export interface Project {
  id: number
  account_id: number
  account_name: string
  created_by: number | null
  created_by_username?: string | null
  account_avatar_url?: string | null
  created_by_avatar_url?: string | null
  title: string
  description: string
  budget_min?: number
  budget_max?: number
  skills: Skill[]
  created_at: string
  updated_at: string
  project_type: 'private' | 'public'
  status: 'open' | 'in_progress' | 'completed' | 'cancelled'
  proposals_count: number
  invitations_count: number
  has_accepted_proposal: boolean
  can_cancel: boolean
}

export interface CreateProjectData {
  title: string
  description: string
  skill_ids: number[]
  budget_min?: number
  budget_max?: number
  account?: number
  project_type?: 'private' | 'public'
}

export type ProjectStatus = Project['status']

export interface PublicProjectSummary {
  id: number
  title: string
  description: string
  status: ProjectStatus
  project_type: 'private' | 'public'
  created_at: string
  updated_at: string
  budget_min?: number | null
  budget_max?: number | null
  account_name: string
  created_by_username: string | null
  account_avatar_url?: string | null
  created_by_avatar_url?: string | null
  skills: Skill[]
  relationship: 'owner' | 'contributor'
}

export interface PublicProfileStatistics {
  projects_posted: number
  open_projects: number
  public_projects: number
  proposals_submitted: number
  proposals_accepted: number
}

export interface PublicProfile {
  id: number
  user: {
    id: number
    username: string
    first_name: string
    last_name: string
  }
  role: 'customer' | 'developer'
  bio?: string | null
  skills: Skill[]
  hourly_rate?: string | null
  company?: string
  location?: string
  email_verified: boolean
  email_verified_at?: string | null
  avatar_url?: string | null
  full_name: string
  member_since: string
  public_projects: PublicProjectSummary[]
  statistics: PublicProfileStatistics
}

export interface ProposalSummary {
  id: number
  account_id: number
  account_name: string
  hourly_rate?: number
  fixed_price?: number
  cover_letter: string
  created_at: string
  accepted: boolean
  submitted_by_username: string
  submitted_by_avatar_url?: string | null
}

export interface ProjectInvitationSummary {
  id: number
  developer_id: number
  developer_username: string
  developer_profile: {
    id: number
    role: string
    bio?: string | null
    hourly_rate?: string | null
    company?: string
    location?: string
    skills: Skill[]
    avatar_url?: string | null
  } | null
  message: string
  status: 'sent' | 'responded' | 'declined'
  created_at: string
  responded_at?: string | null
}

export interface ProjectDetail extends Project {
  proposals: ProposalSummary[]
  invitations: ProjectInvitationSummary[]
}

export interface DeveloperDashboardResponse {
  invited_projects: ProjectDetail[]
  public_projects: Project[]
}

// Paginated response type for Django REST framework
export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// API functions
export const skillsApi = {
  getAll: () => api.get<PaginatedResponse<Skill>>('/accounts/skills/'),
}

export const projectsApi = {
  create: (data: CreateProjectData) => api.post<Project>('/marketplace/projects/', data),
  getAll: () => api.get<PaginatedResponse<Project>>('/marketplace/projects/'),
  get: (projectId: number) => api.get<ProjectDetail>(`/marketplace/projects/${projectId}/`),
  getCustomerDashboard: () =>
    api.get<{ projects: ProjectDetail[] }>('/marketplace/projects/customer-dashboard/'),
  getDeveloperDashboard: () =>
    api.get<DeveloperDashboardResponse>('/marketplace/projects/developer-dashboard/'),
  cancel: (projectId: number) =>
    api.post<ProjectDetail>(`/marketplace/projects/${projectId}/cancel/`),
  invite: (projectId: number, payload: { developer_id: number; message?: string }) =>
    api.post<ProjectInvitationSummary>(`/marketplace/projects/${projectId}/invite/`, payload),
}

export const developersApi = {
  getAll: () => api.get<Profile[]>('/marketplace/matching/developers/'),
}

export const profileApi = {
  get: () => api.get<UserProfileResponse>('/accounts/profiles/me'),
  update: (data: Record<string, unknown>) =>
    api.patch<UserProfileResponse>('/accounts/profiles/me', data),
  uploadAvatar: (file: File) => {
    const formData = new FormData()
    formData.append('avatar', file)
    return api.post<UserProfileResponse>('/accounts/profiles/me/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  removeAvatar: () => api.delete<UserProfileResponse>('/accounts/profiles/me/avatar'),
}

export const profilesApi = {
  getPublic: (username: string) =>
    api.get<PublicProfile>(`/accounts/profiles/public/${encodeURIComponent(username)}/`),
}

// Authentication API functions
export const authApi = {
  csrf: () => api.get<{ csrfToken: string }>('/auth/csrf/'),

  login: (credentials: LoginCredentials) => api.post<AuthResponse>('/auth/login/', credentials),

  signup: (data: Omit<SignupData, 'confirmPassword'> & { confirm_password: string }) =>
    api.post<AuthResponse>('/auth/register/', data),

  logout: () => api.post<{ detail: string }>('/auth/logout/'),

  verifyEmail: (payload: { uid: string; token: string }) =>
    api.post<{ detail: string }>('/auth/verify-email/', payload),

  resendVerification: () => api.post<{ detail: string }>('/auth/verify-email/resend/'),

  requestPasswordReset: (payload: { email: string }) =>
    api.post<{ detail: string }>('/auth/password-reset/', payload),

  confirmPasswordReset: (payload: {
    uid: string
    token: string
    new_password: string
    confirm_password: string
  }) => api.post<{ detail: string }>('/auth/password-reset/confirm/', payload),

  me: () => api.get<{ user: import('@/types/auth').User }>('/auth/me/'),
}

// Messaging types (from generated OpenAPI schema)
export type Message = components['schemas']['MessageSchema']
export type Conversation = components['schemas']['ConversationSchema']
export type MessageCreateData = components['schemas']['MessageCreateSchema']

// Messaging API functions
export const conversationsApi = {
  /**
   * List all conversations for current user
   */
  getAll: () => api.get<Conversation[]>('/marketplace/conversations'),

  /**
   * Get message history for a conversation
   */
  getMessages: (conversationId: number) =>
    api.get<Message[]>(`/marketplace/conversations/${conversationId}/messages`),

  /**
   * Send a message in a conversation
   */
  sendMessage: (conversationId: number, data: MessageCreateData) =>
    api.post<Message>(`/marketplace/conversations/${conversationId}/messages`, data),

  /**
   * Mark all messages in conversation as read
   */
  markAsRead: (conversationId: number) =>
    api.post(`/marketplace/conversations/${conversationId}/mark-read`),
}

// ============================================================================
// Onboarding API
// ============================================================================

export interface OnboardingSchedule {
  id: number
  day_of_week: number
  start_time: string
  end_time: string
  is_active: boolean
}

export interface OnboardingBarber {
  id: number
  submission_id: number
  first_name: string
  last_name: string
  email: string
  phone: string
  bio: string
  specialties: string
  years_experience: number
  instagram_handle: string
  photo_url: string | null
  sort_order: number
  schedules: OnboardingSchedule[]
  created_at: string
  updated_at: string
}

export interface OnboardingService {
  id: number
  submission_id: number
  category_name: string
  name: string
  description: string
  price: string
  duration_minutes: number
  sort_order: number
  created_at: string
  updated_at: string
}

export interface OnboardingFeatureConfig {
  enable_notifications: boolean
  enable_reviews: boolean
  enable_analytics: boolean
  enable_waitlist: boolean
  enable_payments: boolean
  default_reminder_hours_before: number
  email_enabled: boolean
  push_enabled: boolean
}

export interface OnboardingSubmission {
  id: number
  user_id: number
  status: string
  shop_name: string
  tagline: string
  about_text: string
  address: string
  phone: string
  email: string
  primary_color: string
  secondary_color: string
  font_family: string
  social_links: Record<string, string>
  business_hours_text: string
  logo_url: string | null
  deployed_domain: string
  deployed_at: string | null
  deployment_error: string
  payment_reference: string
  barbers_count: number
  services_count: number
  created_at: string
  updated_at: string
}

export interface OnboardingSubmissionSummary {
  id: number
  user_id: number
  status: string
  shop_name: string
  tagline: string
  primary_color: string
  secondary_color: string
  barbers_count: number
  services_count: number
  created_at: string
  updated_at: string
}

export const onboardingApi = {
  createSubmission: (data: { shop_name: string }) =>
    api.post<OnboardingSubmission>('/onboarding/submissions', data),

  listSubmissions: () => api.get<OnboardingSubmissionSummary[]>('/onboarding/submissions'),

  getSubmission: (id: number) => api.get<OnboardingSubmission>(`/onboarding/submissions/${id}`),

  updateSubmission: (id: number, data: Partial<OnboardingSubmission>) =>
    api.patch<OnboardingSubmission>(`/onboarding/submissions/${id}`, data),

  deleteSubmission: (id: number) => api.delete(`/onboarding/submissions/${id}`),

  submitSubmission: (id: number) =>
    api.post<OnboardingSubmission>(`/onboarding/submissions/${id}/submit`),

  revertSubmission: (submissionId: number) =>
    api.post<OnboardingSubmission>(`/onboarding/submissions/${submissionId}/revert`),

  // Barbers
  listBarbers: (submissionId: number) =>
    api.get<OnboardingBarber[]>(`/onboarding/submissions/${submissionId}/barbers`),

  createBarber: (submissionId: number, data: Partial<OnboardingBarber>) =>
    api.post<OnboardingBarber>(`/onboarding/submissions/${submissionId}/barbers`, data),

  updateBarber: (submissionId: number, barberId: number, data: Partial<OnboardingBarber>) =>
    api.patch<OnboardingBarber>(
      `/onboarding/submissions/${submissionId}/barbers/${barberId}`,
      data
    ),

  deleteBarber: (submissionId: number, barberId: number) =>
    api.delete(`/onboarding/submissions/${submissionId}/barbers/${barberId}`),

  // Services
  listServices: (submissionId: number) =>
    api.get<OnboardingService[]>(`/onboarding/submissions/${submissionId}/services`),

  createService: (submissionId: number, data: Partial<OnboardingService>) =>
    api.post<OnboardingService>(`/onboarding/submissions/${submissionId}/services`, data),

  updateService: (submissionId: number, serviceId: number, data: Partial<OnboardingService>) =>
    api.patch<OnboardingService>(
      `/onboarding/submissions/${submissionId}/services/${serviceId}`,
      data
    ),

  deleteService: (submissionId: number, serviceId: number) =>
    api.delete(`/onboarding/submissions/${submissionId}/services/${serviceId}`),

  // Features
  getFeatures: (submissionId: number) =>
    api.get<OnboardingFeatureConfig>(`/onboarding/submissions/${submissionId}/features`),

  updateFeatures: (submissionId: number, data: Partial<OnboardingFeatureConfig>) =>
    api.patch<OnboardingFeatureConfig>(`/onboarding/submissions/${submissionId}/features`, data),

  // Seed config
  getSeedConfig: (submissionId: number) =>
    api.get(`/onboarding/submissions/${submissionId}/seed-config`),

  // Preview
  createPreview: (submissionId: number) =>
    api.post<{ preview_url: string; domain: string; status: string }>(
      `/onboarding/submissions/${submissionId}/preview`
    ),

  paySubmission: (submissionId: number) =>
    api.post<{
      submission_id: number
      status: string
      payment_reference: string
    }>(`/onboarding/submissions/${submissionId}/pay`),

  deploySubmission: (submissionId: number) =>
    api.post<{
      submission_id: number
      status: string
      deployed_domain: string
      deployed_url: string
    }>(`/onboarding/submissions/${submissionId}/deploy`),

  retryDeploy: (submissionId: number) =>
    api.post<{
      submission_id: number
      status: string
      deployed_domain: string
      deployed_url: string
    }>(`/onboarding/submissions/${submissionId}/retry-deploy`),
  uploadLogo: (submissionId: number, file: File) => {
    const formData = new FormData()
    formData.append('logo', file)
    return api.post(`/onboarding/submissions/${submissionId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  uploadBarberPhoto: (submissionId: number, barberId: number, file: File) => {
    const formData = new FormData()
    formData.append('photo', file)
    return api.post(`/onboarding/submissions/${submissionId}/barbers/${barberId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}
